// src/app/services/documentService.ts
import axiosInstance from '@/app/api/axiosInstance';
import { DeliveryChallanResponse } from '@/app/types/deliveryChallan';
import { DocumentStatusEnum } from '@/app/types/document';

export class DocumentService {
    async createDeliveryChallanFromVoucher(voucherId: number): Promise<DeliveryChallanResponse> {
        try {
            const response = await axiosInstance.post<DeliveryChallanResponse>(
                `/vouchers/${voucherId}/create-delivery-challan`
            );
            return response.data;
        } catch (error: unknown) {
            const err = error as Error & { response?: { data?: { detail?: string } } };
            console.error(`Error creating Delivery Challan from Voucher ${voucherId}:`, err);
            throw new Error(err.response?.data?.detail || 'Failed to create Delivery Challan');
        }
    }

    async createDeliveryChallanWithItems(
        voucherId: number,
        data?: {
            vehicle_number?: string;
            transport_mode?: string;
            eway_bill_number?: string;
            assigned_to_party_id?: number;
            notes?: string;
            items?: Array<{
                item_id: number;
                quantity: number;
                voucher_item_id?: number;
            }>;
        }
    ): Promise<DeliveryChallanResponse> {
        try {
            const response = await axiosInstance.post<DeliveryChallanResponse>(
                `/vouchers/${voucherId}/create-delivery-challan`,
                data || {}
            );
            return response.data;
        } catch (error: unknown) {
            const err = error as Error & { response?: { data?: { detail?: string } } };
            console.error(`Error creating Delivery Challan from Voucher ${voucherId}:`, err);
            throw new Error(err.response?.data?.detail || 'Failed to create Delivery Challan');
        }
    }

    async getDeliverableItems(voucherId: number): Promise<Array<{
        item_id: number;
        item_name: string;
        item_code?: string;
        invoice_quantity: number;
        delivered_quantity: number;
        remaining_quantity: number;
        unit_name: string;
        godown_id?: number;
        godown_name?: string;
    }>> {
        try {
            const response = await axiosInstance.get(
                `/vouchers/${voucherId}/deliverable-items`
            );
            return response.data;
        } catch (error: unknown) {
            const err = error as Error & { response?: { data?: { detail?: string } } };
            console.error(`Error fetching deliverable items for Voucher ${voucherId}:`, err);
            throw new Error(err.response?.data?.detail || 'Failed to fetch deliverable items');
        }
    }

    async updateDeliveryChallanStatus(
        dcId: number,
        status: DocumentStatusEnum,
        notes: string = "Status updated via UI"
    ): Promise<DeliveryChallanResponse> {
        try {
            // Using query parameters instead of request body
            const response = await axiosInstance.patch<DeliveryChallanResponse>(
                `/delivery-challans/${dcId}/status`,
                null, // No request body
                {
                    params: {
                        new_status: status,
                        notes: notes
                    }
                }
            );
            return response.data;
        } catch (error: unknown) {
            const err = error as Error & { response?: { data?: { detail?: string } } };
            console.error(`Error updating Delivery Challan ${dcId} status:`, err);
            throw new Error(err.response?.data?.detail || 'Failed to update Delivery Challan status');
        }
    }

    async updateDeliveryChallanAgent(
        dcId: number,
        agentId: number | null
    ): Promise<DeliveryChallanResponse> {
        try {
            const response = await axiosInstance.patch<DeliveryChallanResponse>(
                `/delivery-challans/${dcId}`,
                {
                    assigned_to_party_id: agentId
                }
            );
            return response.data;
        } catch (error: unknown) {
            const err = error as Error & { response?: { data?: { detail?: string } } };
            console.error(`Error updating Delivery Challan ${dcId} agent:`, err);
            throw new Error(err.response?.data?.detail || 'Failed to update Delivery Challan agent');
        }
    }
} 